// src/components/NotificationComposer.js
import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAllUsers } from '../userService';
import UiIcon from './ui/UiIcon';

const NotificationComposer = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetAudience, setTargetAudience] = useState('all');
  const [targetUserId, setTargetUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [category, setCategory] = useState('important');
  const [deepLink, setDeepLink] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (targetAudience === 'user' && users.length === 0) {
      getAllUsers().then(setUsers).catch(console.error);
    }
  }, [targetAudience]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      setError('Title and body are required');
      return;
    }

    if (targetAudience === 'user' && !targetUserId) {
      setError('Select a user to send to');
      return;
    }

    const passkey = prompt('Type the passkey to send this notification:');
    if (passkey === null) return;

    setSending(true);
    setError(null);
    setResult(null);

    try {
      const functions = getFunctions();
      const sendNotification = httpsCallable(functions, 'sendFreeformNotification');

      const payload = {
        title: title.trim(),
        body: body.trim(),
        targetAudience,
        ...(targetAudience === 'user' && { targetUserId }),
        category,
        deepLink: deepLink.trim() || null,
        scheduledFor: scheduledFor ? new Date(scheduledFor).toISOString() : null,
        passkey: passkey.trim(),
      };

      const response = await sendNotification(payload);

      setResult(response.data);

      // Clear form on success
      if (response.data.success) {
        setTitle('');
        setBody('');
        setDeepLink('');
        setScheduledFor('');
      }
    } catch (err) {
      console.error('Failed to send notification:', err);
      setError(err.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h2>Send Notification</h2>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '450' }}>
          Title *
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Notification title"
          maxLength={65}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '6px',
          }}
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          {title.length}/65 characters
        </small>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '450' }}>
          Body *
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Notification body text"
          maxLength={240}
          rows={3}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '6px',
            resize: 'vertical',
          }}
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          {body.length}/240 characters
        </small>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '450' }}>
          Target Audience
        </label>
        <select
          value={targetAudience}
          onChange={(e) => { setTargetAudience(e.target.value); setTargetUserId(''); setUserSearch(''); }}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '6px',
          }}
        >
          <option value="all">All Users</option>
          <option value="subscribers">Subscribers Only</option>
          <option value="free">Free Users Only</option>
          <option value="user">Specific User</option>
        </select>
      </div>

      {targetAudience === 'user' && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '450' }}>
            User
          </label>
          {targetUserId ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                flex: 1,
                padding: '10px',
                fontSize: '14px',
                border: '1px solid #4caf50',
                borderRadius: '6px',
                background: '#f9fff9',
              }}>
                {users.find(u => u.id === targetUserId)?.name} — {users.find(u => u.id === targetUserId)?.email}
              </div>
              <button
                type="button"
                onClick={() => { setTargetUserId(''); setUserSearch(''); }}
                style={{ padding: '10px 14px', fontSize: '13px', cursor: 'pointer', borderRadius: '6px', border: '1px solid #ccc' }}
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by name or email..."
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  marginBottom: '4px',
                }}
              />
              {users.length === 0 ? (
                <div style={{ fontSize: '13px', color: '#888', padding: '8px 0' }}>Loading users...</div>
              ) : (
                <div style={{
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}>
                  {users
                    .filter(u => {
                      if (!userSearch.trim()) return true;
                      const q = userSearch.toLowerCase();
                      return (
                        (u.name || '').toLowerCase().includes(q) ||
                        (u.email || '').toLowerCase().includes(q)
                      );
                    })
                    .map(u => (
                      <div
                        key={u.id}
                        onClick={() => { setTargetUserId(u.id); setUserSearch(''); }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          borderBottom: '1px solid #eee',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <span style={{ fontWeight: '450' }}>{u.name}</span>
                        <span style={{ color: '#666', marginLeft: '8px' }}>{u.email}</span>
                      </div>
                    ))
                  }
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '450' }}>
          Notification Category
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '6px',
          }}
        >
          <option value="important">Tärkeät ilmoitukset (Always sent to all users)</option>
          <option value="news">Uutiset ja päivitykset (Only sent to users who opted in)</option>
          <option value="specialDates">Erikoispäivät (Only sent to users who opted in)</option>
        </select>
        <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
          {category === 'important'
            ? 'These notifications are always sent (e.g., subscription reminders)'
            : category === 'specialDates'
            ? 'Only sent to users who have enabled "Erikoispäivät" in their notification settings'
            : 'Only sent to users who have enabled "Uutiset ja päivitykset" in their notification settings'}
        </small>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '450' }}>
          Deep Link (optional)
        </label>
        <input
          type="text"
          value={deepLink}
          onChange={(e) => setDeepLink(e.target.value)}
          placeholder="e.g., /crossword/25-12-2024, /archive, /subscription"
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '6px',
          }}
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          Leave empty to open home screen
        </small>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '450' }}>
          Schedule (optional)
        </label>
        <input
          type="datetime-local"
          value={scheduledFor}
          onChange={(e) => setScheduledFor(e.target.value)}
          min={new Date().toISOString().slice(0, 16)}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '6px',
          }}
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          Leave empty to send immediately
        </small>
      </div>

      {/* Preview */}
      {(title || body) && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '450' }}>
            Preview
          </label>
          <div
            style={{
              padding: '16px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '1px solid #ddd',
            }}
          >
            <div style={{ fontWeight: '550', marginBottom: '4px', fontSize: '14px' }}>
              {title || 'Title'}
            </div>
            <div style={{ fontSize: '13px', color: '#555' }}>
              {body || 'Notification body text'}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#fee',
            color: '#c33',
            borderRadius: '6px',
            marginBottom: '20px',
          }}
        >
          {error}
        </div>
      )}

      {result && (
        <div
          style={{
            padding: '12px',
            backgroundColor: result.success ? '#efe' : '#fee',
            color: result.success ? '#3c3' : '#c33',
            borderRadius: '6px',
            marginBottom: '20px',
          }}
        >
          {result.scheduled ? (
            <><UiIcon name="check_circle" /> Notification scheduled successfully (ID: {result.notificationId})</>
          ) : (
            <>
              <UiIcon name="check_circle" /> Notification sent to {result.recipientCount} user(s)
              {result.failureCount > 0 && ` (${result.failureCount} failed)`}
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={handleSend}
          disabled={sending || !title.trim() || !body.trim() || (targetAudience === 'user' && !targetUserId)}
          className="btn-primary"
          style={{ flex: 1 }}
        >
          {sending ? 'Sending...' : scheduledFor ? 'Schedule Notification' : 'Send Now'}
        </button>
      </div>
    </div>
  );
};

export default NotificationComposer;
